/**
 * Echo Event Registry — Single Source of Truth
 *
 * Every auditable event is declared exactly once in `ECHO_EVENT_DEFINITIONS`,
 * which is annotated `Record<EchoEventType, EchoEventDefinition>`. That annotation
 * is the whole mechanism: `EchoEventType` is derived from `ECHO_EVENTS`, so the
 * compiler holds the two in step in BOTH directions and there is no second list to
 * drift from.
 *
 *   - an id added to `ECHO_EVENTS` with no entry here          -> TS2741, naming the id
 *   - an entry here for an id `ECHO_EVENTS` does not carry     -> TS2353
 *   - the same id entered twice                                -> TS1117
 *
 * The audit UI reads `ECHO_EVENT_REGISTRY` and the settings page reads
 * `ALL_ECHO_EVENT_IDS`; both are DERIVED here and neither is maintained by hand.
 * An unregistered event has no label, cannot be subscribed to, and — for every org
 * that has ever saved the audit settings page — is not captured at all, because
 * `ALL_ECHO_EVENT_IDS` seeds the enabled set that page persists.
 *
 * Adding a new event:
 *   1. Add the constant to the appropriate ECHO_*_EVENTS object in event-types.ts
 *   2. Add its entry below — the compiler names the id until you do
 *   3. Backend `@Echo()` and the frontend UI pick it up automatically
 */

import {
  ECHO_ACCESS_CARD_EVENTS,
  ECHO_AGENT_EVENTS,
  ECHO_AI_TASK_EVENTS,
  ECHO_API_KEY_CONNECTION_EVENTS,
  ECHO_ASSET_EVENTS,
  ECHO_AUTH_EVENTS,
  ECHO_BUG_REPORT_EVENTS,
  ECHO_CALL_EVENTS,
  ECHO_CHAT_EVENTS,
  ECHO_COLLECTION_EVENTS,
  ECHO_CUSTOM_MODEL_EVENTS,
  ECHO_DEPLOYMENT_EVENTS,
  ECHO_DOCUMENT_EVENTS,
  ECHO_EXTERNAL_TOOL_EVENTS,
  ECHO_KNOWLEDGE_EVENTS,
  ECHO_META_EVENTS,
  ECHO_OAUTH_EVENTS,
  ECHO_ORG_EVENTS,
  ECHO_PAYMENT_EVENTS,
  ECHO_PERMISSION_EVENTS,
  ECHO_PHONE_EVENTS,
  ECHO_SLACK_EVENTS,
  ECHO_SYSTEM_EVENTS,
  ECHO_TEAMS_EVENTS,
  ECHO_TEMPLATE_EVENTS,
  ECHO_USER_EVENTS,
  ECHO_USER_GROUP_EVENTS,
  ECHO_VAULT_EVENTS,
  ECHO_VIBE_CARD_EVENTS,
  ECHO_WHATSAPP_EVENTS,
  ECHO_WORKFLOW_EVENTS,
  ECHO_WORKFLOW_PRESET_EVENTS,
  ECHO_WORKSPACE_EVENTS,
  type EchoEventType
} from "./event-types";

// ─── Types ──────────────────────────────────────────────────

export interface EchoRegistryEvent {
  /** Event ID — typed as EchoEventType so only valid constants compile */
  id: EchoEventType;
  /** Human-readable short label for UI display */
  label: string;
  /** One-line description for tooltips and settings */
  description: string;
}

export interface EchoRegistryCategory {
  /** Machine key for the category (used in filters, config) */
  key: string;
  /** Human-readable category name */
  label: string;
  /** Category description for the settings page */
  description: string;
  /** All auditable events in this category */
  events: EchoRegistryEvent[];
}

// ─── Categories ─────────────────────────────────────────────

/**
 * The display groups, in the order the settings page and both audit-log viewers
 * render them. A category is a heading; the events beneath it are derived.
 */
const ECHO_EVENT_CATEGORY_META = [
  // ─── Authentication & Sessions ────────────────────────────
  {
    key: "authentication",
    label: "Authentication",
    description: "Sign-ins, sign-outs, sessions, and API key usage"
  },
  // ─── API Keys ─────────────────────────────────────────────
  {
    key: "api_keys",
    label: "API Keys",
    description: "API key creation, updates, and revocation"
  },
  // ─── Users ────────────────────────────────────────────────
  {
    key: "users",
    label: "Users",
    description: "User accounts, invitations, and profile changes"
  },
  // ─── Organization ─────────────────────────────────────────
  {
    key: "organization",
    label: "Organization",
    description: "Organization settings, membership, and invitations"
  },
  // ─── Permissions ──────────────────────────────────────────
  {
    key: "permissions",
    label: "Permissions & Access Control",
    description: "Permission grants, revocations, roles, and access policies"
  },
  // ─── Agents ───────────────────────────────────────────────
  {
    key: "agents",
    label: "Agents",
    description: "Agent lifecycle, versions, tools, and configuration"
  },
  // ─── Conversations ────────────────────────────────────────
  {
    key: "conversations",
    label: "Conversations",
    description: "Chat lifecycle, messages, assignments, and ratings"
  },
  // ─── Workflows ────────────────────────────────────────────
  {
    key: "workflows",
    label: "Workflows",
    description: "Workflow design, publishing, execution, and testing"
  },
  // ─── Workflow Presets ─────────────────────────────────────
  {
    key: "workflow_presets",
    label: "Workflow Presets",
    description:
      "Settings-driven workflow generation (e.g. satisfaction evaluation) plus budget-cap enforcement on AI evaluation runs"
  },
  // ─── AI Tasks ─────────────────────────────────────────────
  {
    key: "ai_tasks",
    label: "AI Tasks",
    description: "Task creation, execution, evaluation, and versioning"
  },
  // ─── Documents & Knowledge ────────────────────────────────
  {
    key: "documents",
    label: "Documents & Knowledge",
    description: "File uploads, downloads, processing, and knowledge collections"
  },
  // ─── Assets ───────────────────────────────────────────────
  {
    key: "assets",
    label: "Assets",
    description: "Public asset library uploads and deletions"
  },
  // ─── Knowledge Base ───────────────────────────────────────
  {
    key: "knowledge",
    label: "Knowledge Base",
    description: "Knowledge base uploads, processing, and deletions"
  },
  // ─── Document Templates ───────────────────────────────────
  {
    key: "document_templates",
    label: "Document Templates",
    description: "Template creation, file generation (Word, Excel, PPT)"
  },
  // ─── Workspaces ───────────────────────────────────────────
  {
    key: "workspaces",
    label: "Workspaces",
    description: "Shared file drive creation, renaming, and deletion"
  },
  // ─── Deployments ──────────────────────────────────────────
  {
    key: "deployments",
    label: "Deployments",
    description:
      "Channel creation, configuration, and management, plus the messages agents send out through them"
  },
  // ─── WhatsApp ─────────────────────────────────────────────
  {
    key: "whatsapp",
    label: "WhatsApp",
    description: "Connections, senders, templates, and phone numbers"
  },
  // ─── Slack ────────────────────────────────────────────────
  {
    key: "slack",
    label: "Slack",
    description: "App creation, workspace connections, and bot management"
  },
  // ─── Microsoft Teams ──────────────────────────────────────
  {
    key: "teams",
    label: "Microsoft Teams",
    description: "Bot creation, package publishing, and user imports"
  },
  // ─── Phone Numbers & Voice ────────────────────────────────
  {
    key: "phone",
    label: "Phone Numbers & Voice",
    description: "Number purchases, compliance, A2P registration, and calls"
  },
  // ─── OAuth & Connections ──────────────────────────────────
  {
    key: "oauth",
    label: "OAuth & Connections",
    description: "OAuth flows, provider connections, and disconnections"
  },
  // ─── Vault & Credentials ─────────────────────────────────
  {
    key: "vault",
    label: "Vault & Credentials",
    description: "Secret storage, BYOK keys, and credential management"
  },
  // ─── Access Cards ─────────────────────────────────────────
  {
    key: "access_cards",
    label: "Access Cards",
    description:
      "Scoped delegations of a credential — who was allowed to act as it, and when that stopped"
  },
  // ─── Vibe App Cards ───────────────────────────────────────
  {
    key: "vibe_cards",
    label: "Vibe App Cards",
    description:
      "Access cards imported into a Vibe app's environment — the grant that authorizes and the binding that projects it"
  },
  // ─── External Tools & Integrations ────────────────────────
  {
    key: "external_tools",
    label: "External Tools & Integrations",
    description: "Tool connections, Pipedream integrations, and API connections"
  },
  // ─── Billing & Subscriptions ──────────────────────────────
  {
    key: "billing",
    label: "Billing & Subscriptions",
    description: "Payments, subscriptions, plan changes, and top-ups"
  },
  // ─── Custom Models ────────────────────────────────────────
  {
    key: "custom_models",
    label: "Custom Models",
    description: "Custom AI model registration and configuration"
  },
  // ─── System Operations ────────────────────────────────────
  {
    key: "system_operations",
    label: "System Operations",
    description: "Cron runs, migrations, backups, and platform-level config changes"
  },
  // ─── Bug Reports & Support ───────────────────────────────
  {
    key: "bug_reports",
    label: "Bug Reports",
    description: "Customer-facing bug report submissions"
  },
  // ─── Audit System (Echo Meta) ─────────────────────────────
  {
    key: "audit_system",
    label: "Audit System",
    description:
      "Events emitted by the audit log system itself (config, exports, partitions, alerts)"
  }
] as const satisfies readonly Omit<EchoRegistryCategory, "events">[];

/** The category keys a definition may name. A typo is a compile error. */
type EchoEventCategoryKey = (typeof ECHO_EVENT_CATEGORY_META)[number]["key"];

/** What the registry knows about one event, minus the id that keys it. */
interface EchoEventDefinition {
  /** Which display group this event renders under */
  category: EchoEventCategoryKey;
  /** Human-readable short label for UI display */
  label: string;
  /** One-line description for tooltips and settings */
  description: string;
}

// ─── Definitions ────────────────────────────────────────────

/**
 * Exhaustive over `EchoEventType`. The annotation is load-bearing — see the file
 * header for the three compile errors it produces. Entries are grouped by category
 * and their order here is the order they render in.
 */
const ECHO_EVENT_DEFINITIONS: Record<EchoEventType, EchoEventDefinition> = {
  // ─── Authentication ───
  [ECHO_AUTH_EVENTS.USER_LOGIN]: {
    category: "authentication",
    label: "User login",
    description: "A user signed in"
  },
  [ECHO_AUTH_EVENTS.USER_LOGOUT]: {
    category: "authentication",
    label: "User logout",
    description: "A user signed out"
  },
  [ECHO_AUTH_EVENTS.USER_PASSWORD_CHANGE]: {
    category: "authentication",
    label: "Password change",
    description: "Password was changed"
  },
  [ECHO_AUTH_EVENTS.USER_PASSWORD_RESET]: {
    category: "authentication",
    label: "Password reset",
    description: "Password reset initiated"
  },
  [ECHO_AUTH_EVENTS.SESSION_CREATE]: {
    category: "authentication",
    label: "Session created",
    description: "New session started"
  },
  [ECHO_AUTH_EVENTS.SESSION_EXPIRE]: {
    category: "authentication",
    label: "Session expired",
    description: "Session timed out"
  },
  [ECHO_AUTH_EVENTS.API_AUTHENTICATE]: {
    category: "authentication",
    label: "API authentication",
    description: "API request authenticated"
  },

  // ─── API Keys ───
  [ECHO_AUTH_EVENTS.API_KEY_CREATE]: {
    category: "api_keys",
    label: "Key created",
    description: "A new API key was generated"
  },
  [ECHO_AUTH_EVENTS.API_KEY_UPDATE]: {
    category: "api_keys",
    label: "Key updated",
    description: "API key settings were changed"
  },
  [ECHO_AUTH_EVENTS.API_KEY_REVOKE]: {
    category: "api_keys",
    label: "Key revoked",
    description: "An API key was revoked"
  },

  // ─── Users ───
  [ECHO_USER_EVENTS.CREATE]: {
    category: "users",
    label: "User created",
    description: "New user account created"
  },
  [ECHO_USER_EVENTS.UPDATE]: {
    category: "users",
    label: "User updated",
    description: "User profile changed"
  },
  [ECHO_USER_EVENTS.DELETE]: {
    category: "users",
    label: "User deleted",
    description: "User account deleted"
  },
  [ECHO_USER_EVENTS.INVITE]: {
    category: "users",
    label: "User invited",
    description: "Invitation sent"
  },
  [ECHO_USER_EVENTS.ACTIVATE]: {
    category: "users",
    label: "User activated",
    description: "User account re-enabled"
  },
  [ECHO_USER_EVENTS.DEACTIVATE]: {
    category: "users",
    label: "User deactivated",
    description: "User account disabled and sign-in blocked"
  },
  [ECHO_USER_EVENTS.ROLE_CHANGE]: {
    category: "users",
    label: "User role changed",
    description: "User's role in the organization changed"
  },
  [ECHO_USER_EVENTS.ONBOARDING_COMPLETE]: {
    category: "users",
    label: "Onboarding completed",
    description: "User finished onboarding"
  },
  [ECHO_USER_EVENTS.UI_PREFERENCES_UPDATE]: {
    category: "users",
    label: "Preferences updated",
    description: "UI preferences changed"
  },
  [ECHO_USER_EVENTS.CREDENTIAL_UPDATE]: {
    category: "users",
    label: "Credential saved",
    description: "Provider credential created or updated"
  },
  [ECHO_USER_EVENTS.WEBHOOK_SETTINGS_UPDATE]: {
    category: "users",
    label: "Webhook configured",
    description: "Webhook settings saved"
  },
  [ECHO_USER_EVENTS.MACRO_CREATE]: {
    category: "users",
    label: "Macro created",
    description: "Response macro created"
  },
  [ECHO_USER_EVENTS.MACRO_UPDATE]: {
    category: "users",
    label: "Macro updated",
    description: "Response macro changed"
  },
  [ECHO_USER_EVENTS.MACRO_DELETE]: {
    category: "users",
    label: "Macro deleted",
    description: "Response macro deleted"
  },
  // Admin-user permission lifecycle — distinct events so compliance
  // queries can tell "initial grant" from "policy re-assignment" from
  // "override add/remove" from "full revocation" when filtering by
  // eventType. Same rationale as the POLICY_CREATE/UPDATE/DELETE
  // split under ECHO_PERMISSION_EVENTS.
  [ECHO_USER_EVENTS.PERMISSION_GRANT]: {
    category: "users",
    label: "Admin access granted",
    description: "Admin access granted by assigning an initial policy"
  },
  [ECHO_USER_EVENTS.PERMISSION_POLICY_ASSIGN]: {
    category: "users",
    label: "Admin policy re-assigned",
    description: "Policy governing an existing admin user changed"
  },
  [ECHO_USER_EVENTS.PERMISSION_OVERRIDE_CREATE]: {
    category: "users",
    label: "Permission override added",
    description: "Extra permission granted on top of the user's policy"
  },
  [ECHO_USER_EVENTS.PERMISSION_OVERRIDE_DELETE]: {
    category: "users",
    label: "Permission override removed",
    description: "Extra permission on top of the user's policy removed"
  },
  [ECHO_USER_EVENTS.PERMISSION_REVOKE]: {
    category: "users",
    label: "Admin access revoked",
    description: "All admin access revoked (admin user record deleted)"
  },

  // ─── Organization ───
  [ECHO_ORG_EVENTS.CREATE]: {
    category: "organization",
    label: "Organization created",
    description: "New organization created"
  },
  [ECHO_ORG_EVENTS.DELETE]: {
    category: "organization",
    label: "Organization deleted",
    description: "Organization and all of its data deleted"
  },
  [ECHO_ORG_EVENTS.UPDATE]: {
    category: "organization",
    label: "Settings updated",
    description: "Organization settings changed"
  },
  [ECHO_ORG_EVENTS.MEMBER_ADD]: {
    category: "organization",
    label: "Member added",
    description: "User added to organization"
  },
  [ECHO_ORG_EVENTS.MEMBER_REMOVE]: {
    category: "organization",
    label: "Member removed",
    description: "User removed from organization"
  },
  [ECHO_ORG_EVENTS.MEMBER_ROLE_CHANGE]: {
    category: "organization",
    label: "Role changed",
    description: "Member role was updated"
  },
  [ECHO_ORG_EVENTS.CONFIG_UPDATE]: {
    category: "organization",
    label: "Config updated",
    description: "Organization config modified"
  },
  [ECHO_ORG_EVENTS.RETENTION_POLICY_UPDATE]: {
    category: "organization",
    label: "Retention policy changed",
    description:
      "What happens to the organization's inactive conversations was set or changed — archive, delete or shred"
  },

  // ─── Permissions & Access Control ───
  [ECHO_PERMISSION_EVENTS.GRANT]: {
    category: "permissions",
    label: "Permission granted",
    description: "Access permission was granted"
  },
  [ECHO_PERMISSION_EVENTS.REVOKE]: {
    category: "permissions",
    label: "Permission revoked",
    description: "Access permission was revoked"
  },
  [ECHO_PERMISSION_EVENTS.DECLINE]: {
    category: "permissions",
    label: "Feature declined",
    description: "An admin decided to keep a feature withheld from a group"
  },
  [ECHO_PERMISSION_EVENTS.WILDCARD_GRANT]: {
    category: "permissions",
    label: "Wildcard grant",
    description: "Wildcard permission granted"
  },
  [ECHO_PERMISSION_EVENTS.ACCESS_UPDATE]: {
    category: "permissions",
    label: "Access updated",
    description: "Resource access policy changed"
  },
  [ECHO_PERMISSION_EVENTS.ORG_SETTINGS_UPDATE]: {
    category: "permissions",
    label: "Org settings updated",
    description: "Permission org settings changed"
  },
  [ECHO_PERMISSION_EVENTS.RESOURCE_VISIBILITY_UPDATE]: {
    category: "permissions",
    label: "Resource visibility updated",
    description: "Resource type visibility changed"
  },
  [ECHO_PERMISSION_EVENTS.POLICY_CREATE]: {
    category: "permissions",
    label: "Admin policy created",
    description: "A new admin policy was created"
  },
  [ECHO_PERMISSION_EVENTS.POLICY_UPDATE]: {
    category: "permissions",
    label: "Admin policy updated",
    description: "An admin policy's rules or metadata changed"
  },
  [ECHO_PERMISSION_EVENTS.POLICY_DELETE]: {
    category: "permissions",
    label: "Admin policy deleted",
    description: "An admin policy was deleted"
  },
  [ECHO_USER_GROUP_EVENTS.CREATE]: {
    category: "permissions",
    label: "User group created",
    description: "New user group created"
  },
  [ECHO_USER_GROUP_EVENTS.UPDATE]: {
    category: "permissions",
    label: "User group updated",
    description: "User group settings changed"
  },
  [ECHO_USER_GROUP_EVENTS.DELETE]: {
    category: "permissions",
    label: "User group deleted",
    description: "User group deleted"
  },
  [ECHO_USER_GROUP_EVENTS.MEMBER_ADD]: {
    category: "permissions",
    label: "Member added to group",
    description: "User added to group"
  },
  [ECHO_USER_GROUP_EVENTS.MEMBER_REMOVE]: {
    category: "permissions",
    label: "Member removed from group",
    description: "User removed from group"
  },

  // ─── Agents ───
  [ECHO_AGENT_EVENTS.CREATE]: {
    category: "agents",
    label: "Agent created",
    description: "New agent created"
  },
  [ECHO_AGENT_EVENTS.UPDATE]: {
    category: "agents",
    label: "Agent updated",
    description: "Agent configuration changed"
  },
  [ECHO_AGENT_EVENTS.DELETE]: {
    category: "agents",
    label: "Agent deleted",
    description: "Agent permanently deleted"
  },
  [ECHO_AGENT_EVENTS.DUPLICATE]: {
    category: "agents",
    label: "Agent duplicated",
    description: "Agent was duplicated"
  },
  [ECHO_AGENT_EVENTS.STOP]: {
    category: "agents",
    label: "Agent stopped",
    description: "Agent generation cancelled"
  },
  [ECHO_AGENT_EVENTS.CONFIG_UPDATE]: {
    category: "agents",
    label: "Agent config updated",
    description: "Agent model, tool, or channel configuration changed"
  },
  [ECHO_AGENT_EVENTS.ASSIGN]: {
    category: "agents",
    label: "Agent assigned",
    description: "Agent placed in a folder"
  },
  [ECHO_AGENT_EVENTS.UNASSIGN]: {
    category: "agents",
    label: "Agent unassigned",
    description: "Agent removed from its folder"
  },
  [ECHO_AGENT_EVENTS.PROFILE_PICTURE_UPLOAD]: {
    category: "agents",
    label: "Photo uploaded",
    description: "Profile picture uploaded"
  },
  [ECHO_AGENT_EVENTS.PROFILE_PICTURE_REGENERATE]: {
    category: "agents",
    label: "Photo regenerated",
    description: "AI profile picture regenerated"
  },
  [ECHO_AGENT_EVENTS.VERSION_CREATE]: {
    category: "agents",
    label: "Version created",
    description: "Checkpoint version saved"
  },
  [ECHO_AGENT_EVENTS.VERSION_RESTORE]: {
    category: "agents",
    label: "Version restored",
    description: "Version restored to draft"
  },
  [ECHO_AGENT_EVENTS.VERSION_PUBLISH]: {
    category: "agents",
    label: "Version published",
    description: "Version set as production"
  },
  [ECHO_AGENT_EVENTS.VERSION_DELETE]: {
    category: "agents",
    label: "Version deleted",
    description: "Version deleted"
  },
  [ECHO_AGENT_EVENTS.GOLDEN_CONVERSATION_CREATE]: {
    category: "agents",
    label: "Golden conversation created",
    description: "Golden conversation created for prompt evaluation"
  },
  [ECHO_AGENT_EVENTS.GOLDEN_CONVERSATION_UPDATE]: {
    category: "agents",
    label: "Golden conversation updated",
    description: "Golden conversation authored or curated (turns, checkpoints, ready)"
  },
  [ECHO_AGENT_EVENTS.GOLDEN_CONVERSATION_DELETE]: {
    category: "agents",
    label: "Golden conversation deleted",
    description: "Golden conversation and its turns deleted"
  },
  [ECHO_AGENT_EVENTS.PROMPT_EVAL_RUN_CREATE]: {
    category: "agents",
    label: "Prompt eval run created",
    description:
      "Eval run queued — runs the agent live against every selected checkpoint and calls a judge"
  },
  [ECHO_AGENT_EVENTS.PROMPT_EVAL_RUN_ABORT]: {
    category: "agents",
    label: "Prompt eval run aborted",
    description: "Eval run stopped before it finished; partial cases are kept"
  },
  [ECHO_AGENT_EVENTS.TOOL_CREATE]: {
    category: "agents",
    label: "Tool added",
    description: "Tool config added to agent"
  },
  [ECHO_AGENT_EVENTS.TOOL_UPDATE]: {
    category: "agents",
    label: "Tool updated",
    description: "Tool configuration changed"
  },
  [ECHO_AGENT_EVENTS.TOOL_DELETE]: {
    category: "agents",
    label: "Tool removed",
    description: "Tool removed from agent"
  },
  [ECHO_AGENT_EVENTS.TOOL_DELETE_ALL]: {
    category: "agents",
    label: "All tools removed",
    description: "All tool configs removed from agent"
  },
  [ECHO_AGENT_EVENTS.COLLECTION_ATTACH]: {
    category: "agents",
    label: "Collection attached",
    description: "Knowledge collection attached"
  },
  [ECHO_AGENT_EVENTS.COLLECTION_DETACH]: {
    category: "agents",
    label: "Collection detached",
    description: "Knowledge collection detached"
  },
  [ECHO_AGENT_EVENTS.WORKSPACE_ATTACH]: {
    category: "agents",
    label: "Workspace attached",
    description: "Workspace attached to agent"
  },
  [ECHO_AGENT_EVENTS.WORKSPACE_DETACH]: {
    category: "agents",
    label: "Workspace detached",
    description: "Workspace detached from agent"
  },
  [ECHO_AGENT_EVENTS.SKILL_CREATE]: {
    category: "agents",
    label: "Skill attached",
    description: "Claude Code skill bundle attached to agent"
  },
  [ECHO_AGENT_EVENTS.SKILL_UPDATE]: {
    category: "agents",
    label: "Skill updated",
    description: "Claude Code skill metadata or bundle contents replaced"
  },
  [ECHO_AGENT_EVENTS.SKILL_DELETE]: {
    category: "agents",
    label: "Skill removed",
    description: "Claude Code skill removed from agent"
  },
  [ECHO_AGENT_EVENTS.CODE_INTERPRETER_INVOKED]: {
    category: "agents",
    label: "Code interpreter started",
    description: "Agent started running code in its sandbox"
  },
  [ECHO_AGENT_EVENTS.CODE_INTERPRETER_COMPLETED]: {
    category: "agents",
    label: "Code interpreter finished",
    description: "Agent code run completed successfully"
  },
  [ECHO_AGENT_EVENTS.CODE_INTERPRETER_FAILED]: {
    category: "agents",
    label: "Code interpreter failed",
    description: "Agent code run ended in an error"
  },
  [ECHO_AGENT_EVENTS.ANTHROPIC_PROXY_REQUEST]: {
    category: "agents",
    label: "Model request proxied",
    description: "Agent sent a Claude request through the organization's managed connection"
  },
  [ECHO_AGENT_EVENTS.FOLDER_CREATE]: {
    category: "agents",
    label: "Folder created",
    description: "Agent folder created"
  },
  [ECHO_AGENT_EVENTS.FOLDER_UPDATE]: {
    category: "agents",
    label: "Folder updated",
    description: "Agent folder renamed or moved"
  },
  [ECHO_AGENT_EVENTS.FOLDER_DELETE]: {
    category: "agents",
    label: "Folder deleted",
    description: "Agent folder deleted"
  },

  // ─── Conversations ───
  [ECHO_CHAT_EVENTS.CREATE]: {
    category: "conversations",
    label: "Chat created",
    description: "Conversation started"
  },
  [ECHO_CHAT_EVENTS.UPDATE]: {
    category: "conversations",
    label: "Chat updated",
    description: "Conversation details changed"
  },
  [ECHO_CHAT_EVENTS.DELETE]: {
    category: "conversations",
    label: "Chat deleted",
    description: "Conversation deleted"
  },
  [ECHO_CHAT_EVENTS.SHRED]: {
    category: "conversations",
    label: "Chat shredded",
    description: "Conversation and all of its content permanently removed"
  },
  [ECHO_CHAT_EVENTS.CLOSE]: {
    category: "conversations",
    label: "Chat closed",
    description: "Conversation closed"
  },
  [ECHO_CHAT_EVENTS.REOPEN]: {
    category: "conversations",
    label: "Chat reopened",
    description: "Closed conversation reopened"
  },
  [ECHO_CHAT_EVENTS.ARCHIVE]: {
    category: "conversations",
    label: "Chat archived",
    description: "Conversation moved to the archive"
  },
  [ECHO_CHAT_EVENTS.MEMBER_ADD]: {
    category: "conversations",
    label: "Member added",
    description: "User added to the conversation"
  },
  [ECHO_CHAT_EVENTS.MEMBER_REMOVE]: {
    category: "conversations",
    label: "Member removed",
    description: "User removed from the conversation"
  },
  [ECHO_CHAT_EVENTS.SETTINGS_UPDATE]: {
    category: "conversations",
    label: "Settings updated",
    description: "Chat settings changed"
  },
  [ECHO_CHAT_EVENTS.STATUS_UPDATE]: {
    category: "conversations",
    label: "Status changed",
    description: "Chat status updated (open, closed, pending)"
  },
  [ECHO_CHAT_EVENTS.ASSIGNED_USERS_UPDATE]: {
    category: "conversations",
    label: "Assignment changed",
    description: "Assigned users updated"
  },
  [ECHO_CHAT_EVENTS.COMMENT_ADD]: {
    category: "conversations",
    label: "Comment added",
    description: "Internal comment added to chat"
  },
  [ECHO_CHAT_EVENTS.MESSAGE_SEND]: {
    category: "conversations",
    label: "Message sent",
    description: "Message sent in conversation"
  },
  [ECHO_CHAT_EVENTS.MESSAGE_REGENERATE]: {
    category: "conversations",
    label: "Message regenerated",
    description: "AI response regenerated"
  },
  [ECHO_CHAT_EVENTS.MESSAGE_EDIT]: {
    category: "conversations",
    label: "Message edited",
    description: "Message content changed after it was sent"
  },
  [ECHO_CHAT_EVENTS.MESSAGE_DELETE]: {
    category: "conversations",
    label: "Message deleted",
    description: "Message deleted"
  },
  [ECHO_CHAT_EVENTS.MESSAGE_VALIDATE_SEND]: {
    category: "conversations",
    label: "Message validated & sent",
    description: "Pending message approved and sent"
  },
  [ECHO_CHAT_EVENTS.SATISFACTION_RATE]: {
    category: "conversations",
    label: "Satisfaction rated",
    description: "Message satisfaction score saved"
  },
  [ECHO_CHAT_EVENTS.EXPORT]: {
    category: "conversations",
    label: "Comments exported",
    description: "Chat comments exported as CSV"
  },
  [ECHO_CHAT_EVENTS.WHATSAPP_TEMPLATE_SEND]: {
    category: "conversations",
    label: "WhatsApp template sent",
    description: "Template message sent"
  },

  // ─── Workflows ───
  [ECHO_WORKFLOW_EVENTS.CREATE]: {
    category: "workflows",
    label: "Workflow created",
    description: "New workflow created"
  },
  [ECHO_WORKFLOW_EVENTS.UPDATE]: {
    category: "workflows",
    label: "Workflow updated",
    description: "Workflow design modified"
  },
  [ECHO_WORKFLOW_EVENTS.DELETE]: {
    category: "workflows",
    label: "Workflow deleted",
    description: "Workflow archived"
  },
  [ECHO_WORKFLOW_EVENTS.RESTORE]: {
    category: "workflows",
    label: "Workflow restored",
    description: "Archived workflow restored"
  },
  [ECHO_WORKFLOW_EVENTS.PUBLISH]: {
    category: "workflows",
    label: "Workflow published",
    description: "Workflow made live"
  },
  [ECHO_WORKFLOW_EVENTS.UNPUBLISH]: {
    category: "workflows",
    label: "Workflow unpublished",
    description: "Workflow reverted to draft"
  },
  [ECHO_WORKFLOW_EVENTS.DUPLICATE]: {
    category: "workflows",
    label: "Workflow duplicated",
    description: "Workflow was duplicated"
  },
  [ECHO_WORKFLOW_EVENTS.EXECUTE]: {
    category: "workflows",
    label: "Execution started",
    description: "Workflow run triggered"
  },
  [ECHO_WORKFLOW_EVENTS.CANCEL]: {
    category: "workflows",
    label: "Workflow cancelled",
    description: "Workflow run cancelled"
  },
  [ECHO_WORKFLOW_EVENTS.EXECUTION_CANCEL]: {
    category: "workflows",
    label: "Execution cancelled",
    description: "Running workflow cancelled"
  },
  [ECHO_WORKFLOW_EVENTS.EXECUTION_DELETE]: {
    category: "workflows",
    label: "Execution deleted",
    description: "Execution history deleted"
  },
  [ECHO_WORKFLOW_EVENTS.EXECUTION_RETRIGGER]: {
    category: "workflows",
    label: "Execution retriggered",
    description: "Execution retried with original input"
  },
  [ECHO_WORKFLOW_EVENTS.TEST_START]: {
    category: "workflows",
    label: "Test started",
    description: "Test execution triggered"
  },
  [ECHO_WORKFLOW_EVENTS.NODE_EXECUTE]: {
    category: "workflows",
    label: "Node executed",
    description: "Workflow node ran during an execution"
  },
  [ECHO_WORKFLOW_EVENTS.NODE_FAIL]: {
    category: "workflows",
    label: "Node failed",
    description: "Workflow node ended in an error during an execution"
  },
  [ECHO_WORKFLOW_EVENTS.NODE_TEST]: {
    category: "workflows",
    label: "Node tested",
    description: "Individual node tested"
  },
  [ECHO_WORKFLOW_EVENTS.NODE_RETRY]: {
    category: "workflows",
    label: "Node retried",
    description: "Failed node retried"
  },
  [ECHO_WORKFLOW_EVENTS.TRIGGER_DEPLOY]: {
    category: "workflows",
    label: "Triggers deployed",
    description: "Triggers deployed to environment"
  },
  [ECHO_WORKFLOW_EVENTS.TRIGGER_DELETE]: {
    category: "workflows",
    label: "Triggers deleted",
    description: "Triggers removed"
  },
  [ECHO_WORKFLOW_EVENTS.TRIGGER_SECRET_READ]: {
    category: "workflows",
    label: "Webhook trigger secret viewed",
    description: "The secret that authenticates deliveries to a webhook trigger was read"
  },
  [ECHO_WORKFLOW_EVENTS.TRIGGER_SECRET_ROTATE]: {
    category: "workflows",
    label: "Webhook trigger secret rotated",
    description: "A webhook trigger was issued a new secret; the previous one stopped working"
  },
  [ECHO_WORKFLOW_EVENTS.TRIGGER_SECRET_ENFORCE]: {
    category: "workflows",
    label: "Webhook trigger secret enforced",
    description: "A webhook trigger began refusing deliveries that carry no valid secret"
  },
  [ECHO_WORKFLOW_EVENTS.AGENT_ATTACH]: {
    category: "workflows",
    label: "Attached to agent",
    description: "Workflow attached as agent tool"
  },
  [ECHO_WORKFLOW_EVENTS.AI_GENERATE_CODE]: {
    category: "workflows",
    label: "AI code generated",
    description: "AI generated node code"
  },

  // ─── Workflow Presets ───
  [ECHO_WORKFLOW_PRESET_EVENTS.CREATE]: {
    category: "workflow_presets",
    label: "Preset workflow materialised",
    description: "Preset config produced a managed workflow"
  },
  [ECHO_WORKFLOW_PRESET_EVENTS.UPDATE]: {
    category: "workflow_presets",
    label: "Preset workflow refreshed",
    description: "Preset config changed; managed workflow regenerated"
  },
  [ECHO_WORKFLOW_PRESET_EVENTS.DETACH]: {
    category: "workflow_presets",
    label: "Preset workflow detached",
    description: "Operator detached the managed workflow for direct editing"
  },
  [ECHO_WORKFLOW_PRESET_EVENTS.AI_BUDGET_EXCEEDED]: {
    category: "workflow_presets",
    label: "AI evaluation budget exceeded",
    description: "Pre-flight budget cap short-circuited an AI evaluation run before inference"
  },

  // ─── AI Tasks ───
  [ECHO_AI_TASK_EVENTS.CREATE]: {
    category: "ai_tasks",
    label: "Task created",
    description: "New AI task created"
  },
  [ECHO_AI_TASK_EVENTS.UPDATE]: {
    category: "ai_tasks",
    label: "Task updated",
    description: "AI task updated"
  },
  [ECHO_AI_TASK_EVENTS.DELETE]: {
    category: "ai_tasks",
    label: "Task deleted",
    description: "AI task deleted"
  },
  [ECHO_AI_TASK_EVENTS.DUPLICATE]: {
    category: "ai_tasks",
    label: "Task duplicated",
    description: "AI task duplicated"
  },
  [ECHO_AI_TASK_EVENTS.TEST_EXECUTE]: {
    category: "ai_tasks",
    label: "Test executed",
    description: "AI task test run"
  },
  [ECHO_AI_TASK_EVENTS.PROMPT_UPDATE]: {
    category: "ai_tasks",
    label: "Prompt updated",
    description: "Task prompt modified"
  },
  [ECHO_AI_TASK_EVENTS.MODEL_CONFIG_UPDATE]: {
    category: "ai_tasks",
    label: "Model config updated",
    description: "Task model configuration changed"
  },
  [ECHO_AI_TASK_EVENTS.VERSION_CREATE]: {
    category: "ai_tasks",
    label: "Version created",
    description: "Checkpoint version saved"
  },
  [ECHO_AI_TASK_EVENTS.VERSION_PUBLISH]: {
    category: "ai_tasks",
    label: "Version published",
    description: "Version set as production"
  },
  [ECHO_AI_TASK_EVENTS.VERSION_RESTORE]: {
    category: "ai_tasks",
    label: "Version restored",
    description: "Version restored"
  },
  [ECHO_AI_TASK_EVENTS.VERSION_DELETE]: {
    category: "ai_tasks",
    label: "Version deleted",
    description: "Version deleted"
  },
  [ECHO_AI_TASK_EVENTS.EVALUATION_CREATE]: {
    category: "ai_tasks",
    label: "Evaluation created",
    description: "Evaluation session created"
  },
  [ECHO_AI_TASK_EVENTS.EVALUATION_EXECUTE]: {
    category: "ai_tasks",
    label: "Evaluation executed",
    description: "Evaluation run triggered"
  },
  [ECHO_AI_TASK_EVENTS.EVALUATION_DELETE]: {
    category: "ai_tasks",
    label: "Evaluation deleted",
    description: "Evaluation session deleted"
  },

  // ─── Documents & Knowledge ───
  [ECHO_DOCUMENT_EVENTS.CREATE]: {
    category: "documents",
    label: "Document created",
    description: "Document or folder created"
  },
  [ECHO_DOCUMENT_EVENTS.UPLOAD]: {
    category: "documents",
    label: "File uploaded",
    description: "File uploaded (PDF, CSV, etc.)"
  },
  [ECHO_DOCUMENT_EVENTS.UPDATE]: {
    category: "documents",
    label: "Document updated",
    description: "Document metadata changed"
  },
  [ECHO_DOCUMENT_EVENTS.DELETE]: {
    category: "documents",
    label: "Document deleted",
    description: "Document moved to trash"
  },
  [ECHO_DOCUMENT_EVENTS.MOVE]: {
    category: "documents",
    label: "Document moved",
    description: "Document moved to folder"
  },
  [ECHO_DOCUMENT_EVENTS.DOWNLOAD]: {
    category: "documents",
    label: "Document downloaded",
    description: "Download URL generated"
  },
  [ECHO_DOCUMENT_EVENTS.ACCESS]: {
    category: "documents",
    label: "Document accessed",
    description: "Document, collection, or product documentation read or searched"
  },
  [ECHO_DOCUMENT_EVENTS.SHARE]: {
    category: "documents",
    label: "Document shared",
    description: "Document shared with another user or team"
  },
  [ECHO_DOCUMENT_EVENTS.UNSHARE]: {
    category: "documents",
    label: "Document unshared",
    description: "Document sharing withdrawn"
  },
  [ECHO_DOCUMENT_EVENTS.PROCESS]: {
    category: "documents",
    label: "Processing triggered",
    description: "Document reprocessing started"
  },
  [ECHO_DOCUMENT_EVENTS.WEBSITE_ADD]: {
    category: "documents",
    label: "Website added",
    description: "Website added by URL"
  },
  [ECHO_DOCUMENT_EVENTS.CLOUD_CONNECT]: {
    category: "documents",
    label: "Cloud connected",
    description: "Cloud service (Drive, SharePoint, Notion) connected"
  },
  [ECHO_DOCUMENT_EVENTS.SYNC_ENABLE]: {
    category: "documents",
    label: "Sync enabled",
    description: "Auto-sync enabled"
  },
  [ECHO_DOCUMENT_EVENTS.SYNC_DISABLE]: {
    category: "documents",
    label: "Sync disabled",
    description: "Auto-sync disabled"
  },
  [ECHO_DOCUMENT_EVENTS.SYNC_INTERVAL_UPDATE]: {
    category: "documents",
    label: "Sync interval updated",
    description: "Auto-sync frequency changed"
  },
  [ECHO_DOCUMENT_EVENTS.SYNC_TRIGGER]: {
    category: "documents",
    label: "Sync triggered",
    description: "Manual sync triggered"
  },
  [ECHO_COLLECTION_EVENTS.CREATE]: {
    category: "documents",
    label: "Collection created",
    description: "Knowledge collection created"
  },
  [ECHO_COLLECTION_EVENTS.UPDATE]: {
    category: "documents",
    label: "Collection updated",
    description: "Collection settings changed"
  },
  [ECHO_COLLECTION_EVENTS.DELETE]: {
    category: "documents",
    label: "Collection deleted",
    description: "Collection deleted"
  },
  [ECHO_COLLECTION_EVENTS.DOCUMENT_ADD]: {
    category: "documents",
    label: "Doc added to collection",
    description: "Document added to collection"
  },
  [ECHO_COLLECTION_EVENTS.DOCUMENT_REMOVE]: {
    category: "documents",
    label: "Doc removed from collection",
    description: "Document removed from collection"
  },

  // ─── Assets ───
  [ECHO_ASSET_EVENTS.UPLOAD]: {
    category: "assets",
    label: "Asset uploaded",
    description: "File uploaded to the public asset library"
  },
  [ECHO_ASSET_EVENTS.DELETE]: {
    category: "assets",
    label: "Asset deleted",
    description: "Asset removed from the public asset library"
  },

  // ─── Knowledge Base ───
  [ECHO_KNOWLEDGE_EVENTS.UPLOAD]: {
    category: "knowledge",
    label: "Knowledge uploaded",
    description: "A document or source was added to the knowledge base"
  },
  [ECHO_KNOWLEDGE_EVENTS.PROCESS]: {
    category: "knowledge",
    label: "Knowledge processed",
    description: "Knowledge ingestion and indexing completed"
  },
  [ECHO_KNOWLEDGE_EVENTS.DELETE]: {
    category: "knowledge",
    label: "Knowledge deleted",
    description: "A document or source was removed from the knowledge base"
  },

  // ─── Document Templates ───
  [ECHO_TEMPLATE_EVENTS.CREATE]: {
    category: "document_templates",
    label: "Template created",
    description: "Document template created"
  },
  [ECHO_TEMPLATE_EVENTS.UPDATE]: {
    category: "document_templates",
    label: "Template updated",
    description: "Template configuration changed"
  },
  [ECHO_TEMPLATE_EVENTS.DELETE]: {
    category: "document_templates",
    label: "Template deleted",
    description: "Template deleted"
  },
  [ECHO_TEMPLATE_EVENTS.FILE_UPLOAD]: {
    category: "document_templates",
    label: "Template file uploaded",
    description: "Template file uploaded for parsing"
  },
  [ECHO_TEMPLATE_EVENTS.GENERATE]: {
    category: "document_templates",
    label: "Document generated",
    description: "Document generated from template"
  },
  [ECHO_TEMPLATE_EVENTS.FOLDER_CREATE]: {
    category: "document_templates",
    label: "Folder created",
    description: "Template folder created"
  },
  [ECHO_TEMPLATE_EVENTS.FOLDER_UPDATE]: {
    category: "document_templates",
    label: "Folder updated",
    description: "Template folder renamed or moved"
  },
  [ECHO_TEMPLATE_EVENTS.FOLDER_DELETE]: {
    category: "document_templates",
    label: "Folder deleted",
    description: "Template folder deleted"
  },

  // ─── Workspaces ───
  [ECHO_WORKSPACE_EVENTS.CREATE]: {
    category: "workspaces",
    label: "Workspace created",
    description: "New shared file workspace created"
  },
  [ECHO_WORKSPACE_EVENTS.UPDATE]: {
    category: "workspaces",
    label: "Workspace updated",
    description: "Workspace renamed or its deleted files restored"
  },
  [ECHO_WORKSPACE_EVENTS.DELETE]: {
    category: "workspaces",
    label: "Workspace deleted",
    description: "Workspace and its stored files deleted"
  },

  // ─── Deployments ───
  [ECHO_DEPLOYMENT_EVENTS.CREATE]: {
    category: "deployments",
    label: "Deployment created",
    description: "New channel created"
  },
  [ECHO_DEPLOYMENT_EVENTS.UPDATE]: {
    category: "deployments",
    label: "Deployment updated",
    description: "Channel config changed"
  },
  [ECHO_DEPLOYMENT_EVENTS.RETENTION_POLICY_UPDATE]: {
    category: "deployments",
    label: "Retention policy changed",
    description:
      "What happens to a channel's inactive conversations was set or changed — archive, delete or shred"
  },
  [ECHO_DEPLOYMENT_EVENTS.DELETE]: {
    category: "deployments",
    label: "Deployment deleted",
    description: "Channel deleted"
  },
  [ECHO_DEPLOYMENT_EVENTS.DUPLICATE]: {
    category: "deployments",
    label: "Deployment duplicated",
    description: "Channel copied into a new deployment"
  },
  [ECHO_DEPLOYMENT_EVENTS.ACTIVATE]: {
    category: "deployments",
    label: "Deployment activated",
    description: "Channel switched on and able to receive traffic"
  },
  [ECHO_DEPLOYMENT_EVENTS.DEACTIVATE]: {
    category: "deployments",
    label: "Deployment deactivated",
    description: "Channel switched off and no longer receiving traffic"
  },
  [ECHO_DEPLOYMENT_EVENTS.EXECUTE]: {
    category: "deployments",
    label: "Deployment executed",
    description: "Agent ran in response to a message on this channel"
  },
  [ECHO_DEPLOYMENT_EVENTS.CONNECTION_CREATE]: {
    category: "deployments",
    label: "Connection created",
    description: "Provider connection added to a channel"
  },
  [ECHO_DEPLOYMENT_EVENTS.CONNECTION_UPDATE]: {
    category: "deployments",
    label: "Connection updated",
    description: "Provider connection credentials or settings changed"
  },
  [ECHO_DEPLOYMENT_EVENTS.CONNECTION_DELETE]: {
    category: "deployments",
    label: "Connection deleted",
    description: "Provider connection removed from a channel"
  },
  [ECHO_DEPLOYMENT_EVENTS.TEAM_ASSIGN]: {
    category: "deployments",
    label: "Team assigned",
    description: "Team given access to a channel"
  },
  [ECHO_DEPLOYMENT_EVENTS.TEAM_REMOVE]: {
    category: "deployments",
    label: "Team removed",
    description: "Team access to a channel withdrawn"
  },
  [ECHO_DEPLOYMENT_EVENTS.SENDER_DETACHED]: {
    category: "deployments",
    label: "Sender detached",
    description: "WhatsApp sender reassigned away from this channel"
  },
  [ECHO_DEPLOYMENT_EVENTS.EMAIL_SEND]: {
    category: "deployments",
    label: "Email sent",
    description: "Agent sent an email from a channel"
  },
  [ECHO_DEPLOYMENT_EVENTS.SLACK_SEND]: {
    category: "deployments",
    label: "Slack message sent",
    description: "Agent sent a Slack message from a channel"
  },
  [ECHO_DEPLOYMENT_EVENTS.WEBHOOK_TRIGGER]: {
    category: "deployments",
    label: "Webhook triggered",
    description: "Outgoing webhook fired from a channel"
  },
  [ECHO_DEPLOYMENT_EVENTS.FOLDER_CREATE]: {
    category: "deployments",
    label: "Folder created",
    description: "Deployment folder created"
  },
  [ECHO_DEPLOYMENT_EVENTS.FOLDER_UPDATE]: {
    category: "deployments",
    label: "Folder updated",
    description: "Deployment folder renamed or moved"
  },
  [ECHO_DEPLOYMENT_EVENTS.FOLDER_DELETE]: {
    category: "deployments",
    label: "Folder deleted",
    description: "Deployment folder deleted"
  },

  // ─── WhatsApp ───
  [ECHO_WHATSAPP_EVENTS.CONNECTION_CREATE]: {
    category: "whatsapp",
    label: "Connection created",
    description: "Twilio connection created"
  },
  [ECHO_WHATSAPP_EVENTS.CONNECTION_UPDATE]: {
    category: "whatsapp",
    label: "Connection updated",
    description: "Connection settings changed"
  },
  [ECHO_WHATSAPP_EVENTS.CONNECTION_DELETE]: {
    category: "whatsapp",
    label: "Connection deleted",
    description: "Connection deleted"
  },
  [ECHO_WHATSAPP_EVENTS.SENDER_CREATE]: {
    category: "whatsapp",
    label: "Sender created",
    description: "WhatsApp sender created"
  },
  [ECHO_WHATSAPP_EVENTS.SENDER_DELETE]: {
    category: "whatsapp",
    label: "Sender deleted",
    description: "WhatsApp sender deleted"
  },
  [ECHO_WHATSAPP_EVENTS.SENDER_PROFILE_UPDATE]: {
    category: "whatsapp",
    label: "Sender profile updated",
    description: "Sender profile changed"
  },
  [ECHO_WHATSAPP_EVENTS.SENDER_VERIFY]: {
    category: "whatsapp",
    label: "Verification submitted",
    description: "Sender verification submitted"
  },
  [ECHO_WHATSAPP_EVENTS.SENDER_WEBHOOK_REPOINT]: {
    category: "whatsapp",
    label: "Sender webhook repointed",
    description: "Sender inbound webhook set to its connection's route"
  },
  [ECHO_WHATSAPP_EVENTS.TEMPLATE_CREATE]: {
    category: "whatsapp",
    label: "Template created",
    description: "Message template created"
  },
  [ECHO_WHATSAPP_EVENTS.TEMPLATE_DELETE]: {
    category: "whatsapp",
    label: "Template deleted",
    description: "Message template deleted"
  },
  [ECHO_WHATSAPP_EVENTS.TEMPLATE_SUBMIT_APPROVAL]: {
    category: "whatsapp",
    label: "Template submitted",
    description: "Template submitted for approval"
  },

  // ─── Slack ───
  [ECHO_SLACK_EVENTS.APP_CREATE]: {
    category: "slack",
    label: "App created",
    description: "Slack app created"
  },
  [ECHO_SLACK_EVENTS.APP_DELETE]: {
    category: "slack",
    label: "App deleted",
    description: "Slack app deleted"
  },
  [ECHO_SLACK_EVENTS.APP_INSTALL]: {
    category: "slack",
    label: "App installed",
    description: "Slack app OAuth completed"
  },
  [ECHO_SLACK_EVENTS.WORKSPACE_CONNECT]: {
    category: "slack",
    label: "Workspace connected",
    description: "Slack workspace connected"
  },
  [ECHO_SLACK_EVENTS.WORKSPACE_DELETE]: {
    category: "slack",
    label: "Workspace disconnected",
    description: "Workspace connection removed"
  },
  [ECHO_SLACK_EVENTS.BOT_CREATE]: {
    category: "slack",
    label: "Bot created",
    description: "Slack bot created"
  },
  [ECHO_SLACK_EVENTS.BOT_PROFILE_UPDATE]: {
    category: "slack",
    label: "Bot profile updated",
    description: "Bot profile changed"
  },
  [ECHO_SLACK_EVENTS.MANIFEST_SYNC]: {
    category: "slack",
    label: "Manifest synced",
    description: "Slack manifest synced"
  },

  // ─── Microsoft Teams ───
  [ECHO_TEAMS_EVENTS.BOT_CREATE]: {
    category: "teams",
    label: "Bot created",
    description: "Teams bot created"
  },
  [ECHO_TEAMS_EVENTS.PACKAGE_PUBLISH]: {
    category: "teams",
    label: "Package published",
    description: "Teams app package published"
  },
  [ECHO_TEAMS_EVENTS.USERS_BULK_IMPORT]: {
    category: "teams",
    label: "Users bulk imported",
    description: "Teams users bulk imported"
  },

  // ─── Phone Numbers & Voice ───
  [ECHO_PHONE_EVENTS.NUMBER_BUY]: {
    category: "phone",
    label: "Number purchased",
    description: "Phone number purchased"
  },
  [ECHO_PHONE_EVENTS.NUMBER_TRANSFER]: {
    category: "phone",
    label: "Number transferred",
    description: "Phone number transferred"
  },
  [ECHO_PHONE_EVENTS.NUMBER_RELEASE]: {
    category: "phone",
    label: "Number released",
    description: "Phone number released back to the provider"
  },
  [ECHO_PHONE_EVENTS.NUMBER_MARK_FOR_RELEASE]: {
    category: "phone",
    label: "Number marked for release",
    description: "Phone number marked for release, cooling window started"
  },
  [ECHO_PHONE_EVENTS.NUMBER_CANCEL_PENDING_RELEASE]: {
    category: "phone",
    label: "Pending release cancelled",
    description: "A marked-for-release phone number was restored to active"
  },
  [ECHO_PHONE_EVENTS.NUMBER_CONFIGURE]: {
    category: "phone",
    label: "Number webhooks configured",
    description: "Voice, SMS, or status-callback webhook repointed"
  },
  [ECHO_PHONE_EVENTS.COMPLIANCE_SUBMIT]: {
    category: "phone",
    label: "Compliance submitted",
    description: "Compliance bundle submitted"
  },
  [ECHO_PHONE_EVENTS.A2P_SUBMIT]: {
    category: "phone",
    label: "A2P submitted",
    description: "A2P registration submitted"
  },
  [ECHO_PHONE_EVENTS.ADDRESS_CREATE]: {
    category: "phone",
    label: "Address created",
    description: "Address for compliance created"
  },
  [ECHO_PHONE_EVENTS.ADDRESS_DELETE]: {
    category: "phone",
    label: "Address deleted",
    description: "Compliance address deleted"
  },
  [ECHO_PHONE_EVENTS.REQUEST_CREATE]: {
    category: "phone",
    label: "Request created",
    description: "Phone number request created"
  },
  [ECHO_PHONE_EVENTS.REQUEST_APPROVE]: {
    category: "phone",
    label: "Request approved",
    description: "Phone number request approved"
  },
  [ECHO_PHONE_EVENTS.REQUEST_REJECT]: {
    category: "phone",
    label: "Request rejected",
    description: "Phone number request rejected"
  },
  [ECHO_CALL_EVENTS.INITIATE]: {
    category: "phone",
    label: "Call initiated",
    description: "Voice call initiated"
  },
  [ECHO_CALL_EVENTS.END]: {
    category: "phone",
    label: "Call ended",
    description: "Voice call ended"
  },
  [ECHO_CALL_EVENTS.TRANSFER]: {
    category: "phone",
    label: "Call transferred",
    description: "Call transferred to another number"
  },
  [ECHO_CALL_EVENTS.SMS_SEND]: {
    category: "phone",
    label: "SMS sent",
    description: "SMS sent during/after call"
  },
  [ECHO_CALL_EVENTS.RECORDING_ACCESS]: {
    category: "phone",
    label: "Call recording accessed",
    description: "Call recording download link requested through the public API"
  },

  // ─── OAuth & Connections ───
  [ECHO_OAUTH_EVENTS.CONNECTION_CREATE]: {
    category: "oauth",
    label: "OAuth flow started",
    description: "OAuth authorization initiated"
  },
  [ECHO_OAUTH_EVENTS.CONNECTION_DELETE]: {
    category: "oauth",
    label: "Connection deleted",
    description: "OAuth connection removed"
  },

  // ─── Vault & Credentials ───
  [ECHO_VAULT_EVENTS.SECRET_CREATE]: {
    category: "vault",
    label: "Secret stored",
    description: "BYOK secret stored"
  },
  [ECHO_VAULT_EVENTS.SECRET_UPDATE]: {
    category: "vault",
    label: "Secret updated",
    description: "BYOK secret updated"
  },
  [ECHO_VAULT_EVENTS.SECRET_DELETE]: {
    category: "vault",
    label: "Secret deleted",
    description: "BYOK secret deleted"
  },
  [ECHO_VAULT_EVENTS.POOL_CLAIM]: {
    category: "vault",
    label: "Pool slot claimed",
    description: "Organization claimed a credential pool slot"
  },
  [ECHO_VAULT_EVENTS.POOL_RELEASE]: {
    category: "vault",
    label: "Pool slot released",
    description: "Organization released its credential pool slot"
  },
  // Admin-side pool inventory management — distinct from the org-side
  // claim/release lifecycle above. A compliance auditor asking "who
  // added / removed pool capacity?" needs to filter on these rather
  // than on POOL_CLAIM/POOL_RELEASE.
  [ECHO_VAULT_EVENTS.POOL_SLOT_CREATE]: {
    category: "vault",
    label: "Pool slot added",
    description: "Admin added a new credential slot to the pool"
  },
  [ECHO_VAULT_EVENTS.POOL_SLOT_UPDATE]: {
    category: "vault",
    label: "Pool slot updated",
    description: "Admin modified an existing pool slot (display name or secrets)"
  },
  [ECHO_VAULT_EVENTS.POOL_SLOT_DELETE]: {
    category: "vault",
    label: "Pool slot removed",
    description: "Admin removed an unassigned slot from the pool"
  },
  [ECHO_VAULT_EVENTS.POOL_SLOT_FORCE_RELEASE]: {
    category: "vault",
    label: "Pool slot force-released",
    description: "Admin force-released an assigned slot (support action)"
  },

  // ─── Access Cards ───
  [ECHO_ACCESS_CARD_EVENTS.CREATE]: {
    category: "access_cards",
    label: "Access card created",
    description: "A scoped delegation of a credential was created"
  },
  [ECHO_ACCESS_CARD_EVENTS.UPDATE]: {
    category: "access_cards",
    label: "Access card updated",
    description: "An access card's allowed actions or value constraints changed"
  },
  [ECHO_ACCESS_CARD_EVENTS.REVOKE]: {
    category: "access_cards",
    label: "Access card revoked",
    description:
      "An access card stopped authorizing immediately; it is retained so existing audit rows still resolve"
  },
  [ECHO_ACCESS_CARD_EVENTS.DELETE]: {
    category: "access_cards",
    label: "Access card deleted",
    description: "An access card was removed, cascading every grant built on it"
  },

  // ─── Vibe App Cards ───
  [ECHO_VIBE_CARD_EVENTS.IMPORT]: {
    category: "vibe_cards",
    label: "Card imported into an app",
    description:
      "An access card was granted to a Vibe app and projected into its environment under a name"
  },
  [ECHO_VIBE_CARD_EVENTS.BINDING_UPDATE]: {
    category: "vibe_cards",
    label: "Card binding updated",
    description: "An imported card's environment name or scope changed"
  },
  [ECHO_VIBE_CARD_EVENTS.BINDING_DELETE]: {
    category: "vibe_cards",
    label: "Card binding removed",
    description: "An imported card was removed from a Vibe app's environment"
  },
  [ECHO_VIBE_CARD_EVENTS.GRANT_APPROVE]: {
    category: "vibe_cards",
    label: "Card grant approved",
    description:
      "A second approver activated a grant that was held pending under separation of duty"
  },
  [ECHO_VIBE_CARD_EVENTS.GRANT_TRANSITION]: {
    category: "vibe_cards",
    label: "Card grant state changed",
    description:
      "A Vibe app's authority to use a card changed state, carrying the reason that triggered it"
  },

  // ─── External Tools & Integrations ───
  [ECHO_EXTERNAL_TOOL_EVENTS.CREATE]: {
    category: "external_tools",
    label: "Tool created",
    description: "External tool created from manifest/OpenAPI"
  },
  [ECHO_EXTERNAL_TOOL_EVENTS.UPDATE]: {
    category: "external_tools",
    label: "Tool updated",
    description: "External tool configuration changed"
  },
  [ECHO_EXTERNAL_TOOL_EVENTS.DELETE]: {
    category: "external_tools",
    label: "Tool deleted",
    description: "External tool deleted"
  },
  [ECHO_EXTERNAL_TOOL_EVENTS.CREDENTIAL_DELETE]: {
    category: "external_tools",
    label: "Tool credential deleted",
    description: "Tool credential removed"
  },
  [ECHO_EXTERNAL_TOOL_EVENTS.PIPEDREAM_CREDENTIAL_CREATE]: {
    category: "external_tools",
    label: "Pipedream credential created",
    description: "Pipedream tool credential created"
  },
  [ECHO_EXTERNAL_TOOL_EVENTS.PIPEDREAM_TRIGGER_DELETE]: {
    category: "external_tools",
    label: "Pipedream trigger deleted",
    description: "Pipedream trigger removed"
  },
  [ECHO_API_KEY_CONNECTION_EVENTS.CREATE]: {
    category: "external_tools",
    label: "API connection created",
    description: "API key connection created"
  },
  [ECHO_API_KEY_CONNECTION_EVENTS.DELETE]: {
    category: "external_tools",
    label: "API connection deleted",
    description: "API key connection removed"
  },

  // ─── Billing & Subscriptions ───
  [ECHO_PAYMENT_EVENTS.CHECKOUT_CREATE]: {
    category: "billing",
    label: "Checkout created",
    description: "Checkout session created"
  },
  [ECHO_PAYMENT_EVENTS.CREATE]: {
    category: "billing",
    label: "Payment created",
    description: "Payment recorded against the account"
  },
  [ECHO_PAYMENT_EVENTS.PROCESS]: {
    category: "billing",
    label: "Payment processed",
    description: "Payment completed successfully"
  },
  [ECHO_PAYMENT_EVENTS.FAIL]: {
    category: "billing",
    label: "Payment failed",
    description: "Payment was declined or could not be taken"
  },
  [ECHO_PAYMENT_EVENTS.REFUND]: {
    category: "billing",
    label: "Payment refunded",
    description: "Payment returned to the customer"
  },
  [ECHO_PAYMENT_EVENTS.SUBSCRIPTION_CREATE]: {
    category: "billing",
    label: "Subscription created",
    description: "New subscription started"
  },
  [ECHO_PAYMENT_EVENTS.SUBSCRIPTION_RENEW]: {
    category: "billing",
    label: "Subscription renewed",
    description: "Subscription renewed for another billing period"
  },
  [ECHO_PAYMENT_EVENTS.TOPUP]: {
    category: "billing",
    label: "Account topped up",
    description: "Credits added to account"
  },
  [ECHO_PAYMENT_EVENTS.AUTO_TOPUP_TOGGLE]: {
    category: "billing",
    label: "Auto top-up toggled",
    description: "Automatic top-up enabled/disabled"
  },
  [ECHO_PAYMENT_EVENTS.SUBSCRIPTION_UPGRADE]: {
    category: "billing",
    label: "Plan upgraded",
    description: "Subscription upgraded to Pro"
  },
  [ECHO_PAYMENT_EVENTS.SUBSCRIPTION_DOWNGRADE]: {
    category: "billing",
    label: "Plan downgraded",
    description: "Subscription downgraded to Hobby"
  },
  [ECHO_PAYMENT_EVENTS.SUBSCRIPTION_CANCEL]: {
    category: "billing",
    label: "Subscription cancelled",
    description: "Subscription cancelled"
  },

  // ─── Custom Models ───
  [ECHO_CUSTOM_MODEL_EVENTS.CREATE]: {
    category: "custom_models",
    label: "Model registered",
    description: "Custom model registered"
  },
  [ECHO_CUSTOM_MODEL_EVENTS.UPDATE]: {
    category: "custom_models",
    label: "Model updated",
    description: "Custom model configuration changed"
  },
  [ECHO_CUSTOM_MODEL_EVENTS.DELETE]: {
    category: "custom_models",
    label: "Model deleted",
    description: "Custom model deleted"
  },

  // ─── System Operations ───
  [ECHO_SYSTEM_EVENTS.CRON_RUN]: {
    category: "system_operations",
    label: "Cron job ran",
    description: "A scheduled job executed"
  },
  [ECHO_SYSTEM_EVENTS.MIGRATION_RUN]: {
    category: "system_operations",
    label: "Migration ran",
    description: "A data or schema migration was executed"
  },
  [ECHO_SYSTEM_EVENTS.BACKUP_CREATE]: {
    category: "system_operations",
    label: "Backup created",
    description: "A system backup was created"
  },
  [ECHO_SYSTEM_EVENTS.BACKUP_RESTORE]: {
    category: "system_operations",
    label: "Backup restored",
    description: "A system backup was restored"
  },
  [ECHO_SYSTEM_EVENTS.CONFIG_UPDATE]: {
    category: "system_operations",
    label: "System config updated",
    description: "A platform-level system configuration was changed"
  },

  // ─── Bug Reports ───
  [ECHO_BUG_REPORT_EVENTS.SUBMIT]: {
    category: "bug_reports",
    label: "Bug report submitted",
    description: "A user submitted a bug report (with optional screenshots)"
  },

  // ─── Audit System ───
  [ECHO_META_EVENTS.CONFIG_UPDATE]: {
    category: "audit_system",
    label: "Audit config updated",
    description: "Organization audit logging configuration changed"
  },
  [ECHO_META_EVENTS.EXPORT_CREATE]: {
    category: "audit_system",
    label: "Audit export created",
    description: "An audit log export was requested"
  },
  [ECHO_META_EVENTS.EXPORT_COMPLETE]: {
    category: "audit_system",
    label: "Audit export completed",
    description: "An audit log export finished successfully"
  },
  [ECHO_META_EVENTS.EXPORT_FAILED]: {
    category: "audit_system",
    label: "Audit export failed",
    description: "An audit log export job could not produce a file"
  },
  [ECHO_META_EVENTS.EXPORT_DOWNLOAD]: {
    category: "audit_system",
    label: "Audit export downloaded",
    description: "An audit log export file was fetched via a pre-signed URL"
  },
  [ECHO_META_EVENTS.PARTITION_CREATE]: {
    category: "audit_system",
    label: "Audit partition created",
    description: "A new monthly partition was provisioned for audit log storage"
  },
  [ECHO_META_EVENTS.PARTITION_ARCHIVE]: {
    category: "audit_system",
    label: "Audit partition archived",
    description: "A monthly partition was archived per retention policy"
  },
  [ECHO_META_EVENTS.ALERT_TRIGGER]: {
    category: "audit_system",
    label: "Audit alert triggered",
    description: "An audit policy alert fired (anomaly, threshold, or rule match)"
  }
};

// ─── Derived ────────────────────────────────────────────────

/**
 * `Object.keys` widens to `string[]`; the Record above is exhaustive over
 * `EchoEventType` by construction, so every key is one.
 */
const ECHO_EVENT_IDS = Object.keys(ECHO_EVENT_DEFINITIONS) as EchoEventType[];

/** The registry the audit UI renders, grouped into its display categories. */
export const ECHO_EVENT_REGISTRY: EchoRegistryCategory[] = ECHO_EVENT_CATEGORY_META.map(
  (category) => ({
    key: category.key,
    label: category.label,
    description: category.description,
    events: ECHO_EVENT_IDS.filter((id) => ECHO_EVENT_DEFINITIONS[id].category === category.key).map(
      (id) => ({
        id,
        label: ECHO_EVENT_DEFINITIONS[id].label,
        description: ECHO_EVENT_DEFINITIONS[id].description
      })
    )
  })
);

/** Every registered event id, in registry order. Seeds the settings page. */
export const ALL_ECHO_EVENT_IDS: EchoEventType[] = ECHO_EVENT_REGISTRY.flatMap((category) =>
  category.events.map((event) => event.id)
);
